import React, { lazy, useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { withErrorBoundary } from 'react-error-boundary';
import { FeatureFlags } from 'contexts/Features.context';
import { Roles } from 'contexts/Roles.context';
import SharedBookmarkProvider from 'contexts/SharedBookmark.context';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import * as Layouts from './layouts';
import useNotificationListener from './hooks/useNotificationListener';

const AuditHistory = lazy(() => import('./pages/AuditHistory/AuditHistory'));
const Bookmarks = lazy(() => import('./pages/Bookmarks/Bookmarks'));
const BookmarksGroupShare = lazy(() => import('./pages/BookmarksGroupShare/BookmarksGroupShare'));
const CustomerRequests = lazy(() => import('./pages/CustomerRequests/CustomerRequests'));
const VehicleList = lazy(() => import('./pages/VehicleList/VehicleList'));
const DailySalesCollection = lazy(() =>
  import('./pages/DailySalesCollection/DailySalesCollection'),
);
const OtherDistributors = lazy(() => import('./pages/OtherDistributors/OtherDistributors'));
const Pipeline = lazy(() => import('./pages/Pipeline/Pipeline'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const SalesAndStock = lazy(() => import('./pages/SalesAndStock/SalesAndStock'));
const Vehicle = lazy(() => import('./pages/VehicleDetails/VehicleDetails'));

const Router = () => {
  const auth = useAuth();
  const location = useLocation();
  const { features, fields } = useContext(FeatureFlags);
  const { userInfo } = useContext(Roles);
  const roles = userInfo()?.roles;
  const { isUserInfoLoading, rolesExist } = useContext(Roles);
  const hasPipelinePermissions = rolesExist([
    'CUBE_E2E_ADMIN',
    'CUBE_E2E_GENERAL_INQUIRY',
    'CUBE_E2E_GENERAL_INQUIRY_PII',
  ]);

  // Listen for notification events
  useNotificationListener();

  switch (auth.activeNavigator) {
    case 'signinSilent':
    case 'signinRedirect':
      return <div>Redirecting to login...</div>;
    case 'signoutRedirect': {
      return <div>Redirecting to login...</div>;
    }
  }

  if (!features || isUserInfoLoading) {
    return null;
  }

  const { hostname, pathname, search, hash } = window.location;
  const hasRedirectRole =
    fields?.mfeRedirectEnabled?.roles?.length === 0 ||
    fields?.mfeRedirectEnabled?.roles?.some((role) => roles?.includes(role));

  if (features?.mfeRedirectEnabled && hasRedirectRole && hostname.includes('pipeline.one.cube')) {
    const newHostname = hostname.replace('pipeline.one.cube', 'one.pipeline.cube');

    const skipPipelinePaths = ['/CSTR/DrcDashboard'];
    const shouldSkipPipeline = skipPipelinePaths.some((p) => pathname.includes(p));

    const basePath = shouldSkipPipeline ? '' : '/pipeline';

    const newUrl = `https://${newHostname}${basePath}${pathname}${search}${hash}`;

    window.location.replace(newUrl);

    return null; // prevent 404 render
  }

  if (!isUserInfoLoading && !hasPipelinePermissions && location.pathname !== '/authorization') {
    return <Navigate to="/authorization" replace />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Layouts.Root />}>
          <Route element={<Layouts.VehicleInventory />}>
            <Route index element={<Pipeline />} />
          </Route>
          <Route element={<Layouts.CustomerRequests />}>
            <Route path="customer-requests/:profilePathName?" element={<CustomerRequests />} />
          </Route>
          {features?.otherDistributorsEnabled && (
            <Route element={<Layouts.OtherDistributors />}>
              <Route path="other-distributors" element={<OtherDistributors />} />
            </Route>
          )}
          {features?.salesAndStockEnabled && (
            <Route element={<Layouts.SalesAndStock />}>
              <Route path="sales-and-stock" element={<SalesAndStock />} />
            </Route>
          )}
          {features?.dailySalesCollectionEnabled && (
            <Route element={<Layouts.DailySalesCollection />}>
              <Route path="daily-sales-collection" element={<DailySalesCollection />} />
            </Route>
          )}
          <Route path="vehicle/:modelYear/:urn" element={<Vehicle />} />
          {features?.bookmarksEnabled && (
            <Route element={<SharedBookmarkProvider />}>
              <Route path="bookmarks" element={<Bookmarks />} />
              <Route path="bookmarks/:groupShareId" element={<BookmarksGroupShare />} />
            </Route>
          )}
          {features?.auditLogsEnabled && (
            <Route element={<Layouts.AuditHistory />}>
              <Route path="audit-logs" element={<AuditHistory />} />
            </Route>
          )}
          <Route element={<Layouts.Reports />}>
            <Route path="reports" element={<Reports />} />
          </Route>
          {features?.cnhNotificationsEnabled && (
            <Route element={<Layouts.VehicleList />}>
              <Route path="vehicle-list" element={<VehicleList />} />
            </Route>
          )}

          <Route path="authorization" element={<ErrorPage type={'authorization'} />} />
          <Route path="*" element={<ErrorPage type={'404'} />} />
        </Route>
      </Routes>
    </>
  );
};

export default withErrorBoundary(Router, {
  fallback: <ErrorPage type={'problems'} hideNavBar />,
});
